// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';
import { replace } from 'connected-react-router';
import memoizeOne from 'memoize-one';

import { Helmet } from 'react-helmet';
import queryString from 'query-string';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import { ThemeProvider } from '@material-ui/core/styles';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import CloseIcon from '@material-ui/icons/Close';

import { activateUpdatedApp, isRegistered as swIsRegistered, register as swRegister, unregister as swUnregister } from 'serviceWorker';
import { getCurrentTheme } from 'themes/themesSelectors';

import AccountDrawer from 'components/AccountDrawer';
import { doAccountDiscovery as doAccountDiscoveryAction } from 'components/Dialogs/AccountDiscovery/actions';
import ErrorBoundary from 'components/ErrorBoundary';
import MobileDirectDialog from 'components/MobileDirectDialog';
import QCard from 'components/QCard';
import ResolveDatasetDialog from 'components/ResolveDatasetDialog';
import RootModal from 'components/RootModal';
import SetupStatusBar from 'components/SetupStatusBar';
import WebSocketQCS from 'components/WebSocketQCS';

import { ActionsSnackBar } from 'containers/App/ActionsSnackBar';
import { ErrorHandler } from 'containers/App/ErrorHandler';
import OfflinePage from 'containers/OfflinePage';
import SetupPage from 'containers/SetupPage';
import SetupPageAcme from 'containers/SetupPageAcme';
import UnauthenticatedHomePage from 'containers/UnauthenticatedHomePage/loadable';

import * as accountSelectors from 'data/accounts/selectors';
import { appRefreshAll } from 'data/app/actions';
import { getIsOnline, getServiceWorkerUpdateIsAvailable } from 'data/app/selectors';
import { getDatasetsById, getLoadPending as getDatasetsLoadPending, getError as getDatasetsError } from 'data/datasets/selectors';
import * as entitlementSelectors from 'data/entitlements/entitlementsSelectors';
import * as postponedActionsActions from 'data/postponedActions/postponedActionsActions';
import * as postponedActionsSelectors from 'data/postponedActions/postponedActionsSelectors';
import * as preferencesActions from 'data/preferences/actions';
import * as preferencesSelectors from 'data/preferences/selectors';
import { getProfile } from 'data/profile/selectors';

import { isAuthenticatedOrElse, isAuthenticatedWithDatasetOrElse } from 'utils/auth';
import * as authSelectors from 'utils/auth/selectors';
import Cobrowse from 'utils/cobrowse';
import { getFeatureFlags, getLoadPending } from 'utils/featureFlags/selectors';
import Hotjar from 'utils/hotjar';
import Intercom from 'utils/intercom';
import { getLogger } from 'utils/logger';
import Pendo from 'utils/pendo';
import tracker from 'utils/tracker';

import { isAcme } from 'isAcme';

import UsersnapLoader from './UsersnapLoader';
import Header from './Header';
import Main from './Main';
import Undo from './Undo';
import Notifications from './Notifications';
import { featurePages } from './paths';
import type { FeaturePage } from './paths';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////
/////////

let DevMenu = null;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////
/////////


const wrappedComponents = new Map(featurePages.map((x) => {
  const GetStarted = isAcme ? SetupPageAcme : SetupPage;
  if (x.authNotNeeded) {
    return [x.path, x.component];
  }
  if (x.datasetNotNeeded) {
    return [x.path, (isAuthenticatedOrElse(x.component, UnauthenticatedHomePage))];
  }
  return [x.path, (isAuthenticatedOrElse(
    isAuthenticatedWithDatasetOrElse(x.component, GetStarted),
    UnauthenticatedHomePage
  )),
  ];
}));

const log = getLogger('App');

export class App extends React.PureComponent {

  // we don't want to setup new routes every time when App re-renders
  cachedSwitch = memoizeOne((isAuthenticated, resolveDatasetsNeeded, _featureFlags) => (
    <Switch>
      {featurePages.map((page) => (
        (!page.exclusiveProject || process.env && (page.exclusiveProject.toLowerCase() === process.env.PROJECT.toLowerCase())) && !(isAuthenticated && this.isFeatureDisabled(page)) &&
        <Route
          exact={page.exact}
          key={page.path}
          path={page.path ? [page.path, ...page.pathParams.map((p) => `${page.path}${p}`)] : page.path}
          component={resolveDatasetsNeeded ? ResolveDatasetDialog : wrappedComponents.get(page.path)}
        />
      ))}
    </Switch>
  ));

  constructor(props) {
    super(props);

    // const selFeatureObj = featurePages.find((x) => x.path === props.location.pathname);

    this.state = {
      showUpdatedServiceWorkerIsAvailable: props.serviceWorkerUserUpdate && props.serviceWorkerUpdateAvailable,

      isAccountDrawerOpen : isAcme ? true : props.accountDrawerDefaultOpen, // selFeatureObj && selFeatureObj.open,
      selectedAccountNodes: {
        '/transactions': 'all',
        '/investments': 'INVESTMENT',
      },
      selectedFeature: props.location.pathname,
      showFailedActions: false,
      dimAccountList: false,
      numAccounts: 1,  // assume there are accounts
      qCardsOffByDefault: props.qCardsOffByDefault ? props.qCardsOffByDefault : '{}',
    };

    this.preserveDisplayNode(props, true);

    // if (query.displayNode && props.location.pathname && selectedFeature.preservePath) {
    //   this.state.selectedAccountNodes[props.location.pathname] = query.displayNode;
    // }
    tracker.track(tracker.events.pageView);
  }

  componentDidMount() {
    this.props.dispatchAppRefreshAllAction({context: 'init'});
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.location.pathname !== nextProps.location.pathname) {
      tracker.track(tracker.events.pageView);
    }
    if ((nextProps.location.pathname !== this.state.selectedFeature)
      || (nextProps.location.search !== this.state.search))
    {
      this.preserveDisplayNode(nextProps);
      this.setState({ selectedFeature: nextProps.location.pathname });
    }
    if (!isAcme && (nextProps.accountDrawerDefaultOpen !== this.props.accountDrawerDefaultOpen)) {
      this.setState({ isAccountDrawerOpen: nextProps.accountDrawerDefaultOpen});
    }
    if (nextProps.qCardsOffByDefault && (nextProps.qCardsOffByDefault !== this.state.qCardsOffByDefault)) {
      this.setState({ qCardsOffByDefault: nextProps.qCardsOffByDefault });
    }
    if (nextProps.accountsById.size !== this.state.numAccounts) {
      this.setState({ numAccounts: nextProps.accountsById.size });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (!swIsRegistered() && this.props.serviceWorkerEnable) {
      this.registerServiceWorker();
    } else if (swIsRegistered && !this.props.serviceWorkerEnable) {
      this.unregisterServiceWorker();
    }

    // if updated service worker is available and auto-updateable, then update on a feature change
    if (this.props.serviceWorkerUpdateAvailable && this.props.serviceWorkerAutoUpdate && prevState.selectedFeature !== this.state.selectedFeature) {
      this.activateUpdatedServiceWorker();
    }
    else {
      // eslint-disable-next-line no-lonely-if
      if (this.props.serviceWorkerUpdateAvailable && this.props.serviceWorkerUserUpdate) {
        if (!prevProps.serviceWorkerUpdateAvailable || !prevProps.serviceWorkerUserUpdate) {
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({ showUpdatedServiceWorkerIsAvailable: true });
        }
      }
      else {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ showUpdatedServiceWorkerIsAvailable: false });
      }
    }
  }

  // //////////////////////////////////////////////////////////////////////////////////////////////
  // Service Worker Related
  // //////////////////////////////////////////////////////////////////////////////////////////////

  registerServiceWorker = () => {
    swRegister();
  };

  unregisterServiceWorker = () => {
    swUnregister();
  };

  activateUpdatedServiceWorker = () => {
    activateUpdatedApp();
    this.setState({ showUpdatedServiceWorkerIsAvailable: false });
  };

  // //////////////////////////////////////////////////////////////////////////////////////////////
  // Other
  // //////////////////////////////////////////////////////////////////////////////////////////////

  preserveDisplayNode(props, init = false) {
    const query = queryString.parse(props.location.search);
    if (query.displayNode) {
      const newNodes = this.state.selectedAccountNodes;
      newNodes[props.location.pathname] = query.displayNode;
      if (init) {
        this.state = { ...this.state, selectedAccountNodes: newNodes };
      } else {
        this.setState({ selectedAccountNodes: newNodes });
      }

      // this.state.selectedAccountNodes[props.location.pathname] = query.displayNode;
    }
  };

  drawerChange = (open) => {
    this.setState({ isAccountDrawerOpen: open });
    //
    this.props.setPreference({ section: 'shared', group: 'dataset', preference: { webApp: { accountDrawerDefaultOpen: open }}});
  };

  handleAccountNodeSelect = (node) => {
    this.props.history.push(`${node.routeTo}?displayNode=${node.id}`);
  };

  isFeatureDisabled = (featurePage: FeaturePage) => {
    let featureDisabled = false;

    if (this.props.featureFlags && this.props.featureFlags.size > 0 && featurePage.featureFlag && featurePage.featureFlag.length > 0) {
      const featureFlag = this.props.featureFlags.get(featurePage.featureFlag);
      if (featureFlag !== undefined) {
        featureDisabled = Boolean(!featureFlag);
      }
    }

    return featureDisabled;
  };

  headerMouseEnter = () => {
    this.setState({ dimAccountList: true });
  };
  headerMouseLeave = () => {
    this.setState({ dimAccountList: false });
  };

  qCardsObj = () => {
    if (this.state.qCardsOffByDefault) {
      return JSON.parse(this.state.qCardsOffByDefault);
    }
    return {};
  };

  qCardsSetOffState = (path, forceSetting) => {

    const newVal = (forceSetting !== undefined) ? forceSetting : !this.qCardsObj()[path];
    const newObject = { ...this.qCardsObj(), [path]: newVal};
    this.props.setPreference({ section: 'shared', group: 'dataset', preference: { webApp: { qCardsOffByDefault: JSON.stringify(newObject) }}});
    this.setState({
      qCardsOffByDefault: JSON.stringify(newObject),
    });
  };

  handleCloseServiceAvailable = () => {
    this.setState({ showUpdatedServiceWorkerIsAvailable: false });
  };

  renderUpdatedServiceWorkerAvailable = () => {
    const { showUpdatedServiceWorkerIsAvailable } = this.state;

    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={showUpdatedServiceWorkerIsAvailable}
        autoHideDuration={30000}
        message="Updated App is Available"
        action={
          <>
            <Button color="secondary" size="small" onClick={this.activateUpdatedServiceWorker}>
              UPDATE
            </Button>
            <IconButton size="small" aria-label="close" color="inherit" onClick={this.handleCloseServiceAvailable}>
              <CloseIcon fontSize="small"/>
            </IconButton>
          </>
        }
      />
    );
  };

  render() {

    if (this.props.isOffline || !navigator.onLine) {
      return (
        <ThemeProvider theme={this.props.muiTheme}>
          <OfflinePage />;
        </ThemeProvider>
      );
    }

    const qCardsOnFlag = this.props.featureFlags.get('qCardsOn');

    const { selectedAccountNodes, dimAccountList } = this.state;
    const selectedAccountNodeId = selectedAccountNodes[this.props.location.pathname];

    const selectedFeature = featurePages.find((x) => x.path === this.state.selectedFeature) || {};
    const {isAuthenticated, accountsLoadPending, accountsById, accountDrawerDefaultOpen, datasetId,
      launchPad, datasetsById, setupStateIndex, flagsLoadPending, preferencesLoading, datasetError, datasetsLoadPending } = this.props;
    const currentDataset = datasetId && datasetsById.get(datasetId);

    const resolveDatasetsNeeded = !selectedFeature.authNotNeeded && isAuthenticated && datasetsLoadPending && datasetsById.size === 0 && datasetError;

    // Acme Getting-started-redirect on no-accounts
    if (isAcme && selectedFeature.path !== '/get-started' && !selectedFeature.authNotNeeded && isAuthenticated &&
      Boolean(datasetId) && !resolveDatasetsNeeded &&
      ((!accountsLoadPending && accountsById && accountsById.size === 0) || (!preferencesLoading?.shared?.dataset && typeof setupStateIndex === 'number'))) {
      this.props.history.push('/get-started');
    }

    // Quicken getting-started redirects
    if(!isAcme && isAuthenticated && this.props.zeroStateOnNoAccounts && !datasetsLoadPending && !resolveDatasetsNeeded) {

      // Setup page might redirect to platformSelector, so we much check for that case here to not keep redirecting back and forth
      if (datasetsById.size === 0 && selectedFeature.path && selectedFeature.path !== '/platformSelector' && selectedFeature.path !== '/setup') {
        log.log('**APP redirect => /setup');
        log.log(selectedFeature.path);
        this.props.redirect('/setup');
        return <div/>;
      }

      if(datasetsById.size >= 1 && currentDataset && !accountsLoadPending && accountsById &&
        !selectedFeature.ignoreZeroState && !selectedFeature.datasetNotNeeded) {
        if (['QWIN', 'QMAC'].includes(currentDataset.platform)) {
          if (currentDataset.disableRemoteAccess) { // user has desktop dataset, but sync is OFF
            log.log('**APP redirect => (guide user to go to desktop and enable sync ON)');
            // (guide user to go to desktop and enable sync ON)
          } else if(accountsById.size === 0 && selectedFeature.path !== '/setup') {
            log.log('**APP redirect => /setup');
            this.props.history.push('/setup');
          } else if(accountsById.size >= 1) { log.log('**APP redirect => dashboard1'); } // 'show dashboard' // do nothing?
        }
        if (['QWIN_MWF', 'QMAC_MWF', 'QWIN_CA_MWF'].includes(currentDataset.platform)) {
          if (accountsById.size === 0 && !this.props.entitlementIsLoading && this.props.entitlement && selectedFeature.path !== '/getting-started-webfirst') {
            log.log('**APP redirect => /getting-started-webfirst');
            setTimeout(() => this.props.history.push('/getting-started-webfirst'), 10);
          } else if(accountsById.size >= 1) { log.log('**APP redirect => dashboard2'); } // 'show dashboard' // do nothing?
        }
      }
    }

    const showSetupBar = this.props.datasetsById && (this.props.datasetsById.size > 0) && this.props.datasetId;
    const SetupBarComponent = SetupStatusBar; // isAuthenticatedWithDatasetOrElse(SetupStatusBar, React.Fragment);
    const qCardsOn = !this.qCardsObj()[selectedFeature.path] && !flagsLoadPending && !preferencesLoading.shared.dataset;

    return (
      <ThemeProvider theme={this.props.muiTheme}>
        <StyledComponentsThemeProvider theme={this.props.muiTheme}>
          <>
            {this.renderUpdatedServiceWorkerAvailable()}
            {selectedFeature.qCards && qCardsOn && qCardsOnFlag &&
              <QCard
                name="app"
                open={qCardsOn && qCardsOnFlag}
                initialCard="firstCard"
                cards={selectedFeature.qCards}
                onClose={() => this.qCardsSetOffState(selectedFeature.path, true)}
              />
            }
            <Main
              {...this.props}
            >
              <Helmet defaultTitle={this.props.muiTheme.project} titleTemplate={`${this.props.muiTheme.project} - %s`}>
                <meta name="description" content={`${this.props.muiTheme.project} Web App`}/>
                <link rel="icon" href="favicon.ico"/>
                <link rel="apple-touch-icon" href="apple-touch-icon.png"/>
              </Helmet>

              <ErrorBoundary>
                <ActionsSnackBar
                  showFailedActionsHandler={() => this.setState({ showFailedActions: true })}
                  postponedActions={this.props.postponedActions}
                  dispatchDismissPostponedActionsAction={this.props.dispatchDismissPostponedActionsAction}
                  dispatchDispatchPostponedActionsAction={this.props.dispatchDispatchPostponedActionsAction}
                  theme={this.props.muiTheme}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <ErrorHandler
                  open={this.state.showFailedActions}
                  onClose={() => this.setState({ showFailedActions: false })}
                  postponedActions={this.props.postponedActions}
                />
              </ErrorBoundary>
              <ErrorBoundary>
                {this.props.hotjar && <Hotjar/>}
              </ErrorBoundary>
              <ErrorBoundary>
                <Header
                  showRefreshIcon={isAcme || !this.state.isAccountDrawerOpen}
                  selected={this.state.selectedFeature}
                  selectedAccountNodes={this.state.selectedAccountNodes}
                  onMouseEnter={this.headerMouseEnter}
                  onMouseLeave={this.headerMouseLeave}
                  qCardsOn={qCardsOn}
                  onQCardsClick={(qCardsOnFlag && selectedFeature.qCards) ?
                    (val) => this.qCardsSetOffState(selectedFeature.path, !val) : null}
                  hide={selectedFeature.hideNav || resolveDatasetsNeeded}
                />
              </ErrorBoundary>
              <div
                style={isAuthenticated && datasetId ?
                  { marginLeft: this.props.muiTheme.components.navigation.barWidth, display: 'flex', position: 'relative', height:'100vh' } : {}}
                onMouseMove={() => this.setState({ dimAccountList: false })}
              >
                {isAuthenticated && datasetId && selectedFeature.accountShowing &&
                  <AccountDrawer
                    dimAccountList={dimAccountList}
                    history={this.props.history}
                    open
                    selectedNode={selectedFeature.useSelectedAccount && selectedAccountNodeId}
                    drawerChange={this.drawerChange}
                    nodeSelect={this.handleAccountNodeSelect}
                    doAccountDiscovery={this.props.addFi ? () => this.props.doAccountDiscovery({ initiator: selectedFeature.addFIInitiator }) : undefined}
                    accountDrawerDefaultOpen={accountDrawerDefaultOpen}
                  />
                }
                <ErrorBoundary>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      height: '100vh',
                      backgroundColor: this.props.muiTheme.palette.background.default,
                      overflowY: selectedFeature.noScroll ? null : 'scroll',
                    }}
                  >
                    {selectedFeature.showSetupBar &&
                      !isAcme &&
                      launchPad &&
                      showSetupBar &&
                    this.props.isAuthenticated &&
                      <div id="app-main-header-area" style={{ flex: 0 }} >
                        <SetupBarComponent
                          key="setupStatusMainKey"
                          name="appmain"
                        />
                      </div>
                    }
                    <div id="app-main-feature-frame" style={{ flex: 1, height: '100%' }} >
                      {this.cachedSwitch(isAuthenticated, resolveDatasetsNeeded, this.props.featureFlags)}
                    </div>
                  </div>
                </ErrorBoundary>
              </div>
              <ErrorBoundary>
                <UsersnapLoader/>
              </ErrorBoundary>
              <ErrorBoundary>
                {DevMenu && <DevMenu/>}
              </ErrorBoundary>
              <ErrorBoundary>
                <RootModal/>
              </ErrorBoundary>
              <ErrorBoundary>
                <MobileDirectDialog
                  isAuthenticated={isAuthenticated}
                />
              </ErrorBoundary>
              <ErrorBoundary>
                <Undo/>
              </ErrorBoundary>
              <ErrorBoundary>
                <Notifications/>
              </ErrorBoundary>
              <ErrorBoundary>
                {this.props.showIntercom && <Intercom/>}
              </ErrorBoundary>
              <ErrorBoundary>
                <Cobrowse/>
              </ErrorBoundary>
              <ErrorBoundary>
                <Pendo/>
              </ErrorBoundary>
              <ErrorBoundary>
                <WebSocketQCS/>
              </ErrorBoundary>
            </Main>
          </>
        </StyledComponentsThemeProvider>
      </ThemeProvider>
    );
  };
}

App.propTypes = {
  isOffline: PropTypes.bool,

  serviceWorkerEnable: PropTypes.bool,
  serviceWorkerUpdateAvailable: PropTypes.bool,
  serviceWorkerAutoUpdate: PropTypes.bool,
  serviceWorkerUserUpdate: PropTypes.bool,

  updateAppCB: PropTypes.func,

  history: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  datasetId: PropTypes.string,
  qCardsOffByDefault: PropTypes.string,
  datasetsById: PropTypes.object,
  datasetsLoadPending: PropTypes.bool,
  datasetError: PropTypes.object,
  doAccountDiscovery: PropTypes.func,
  muiTheme: PropTypes.object,
  postponedActions: PropTypes.object,
  accountsById: PropTypes.object,
  dispatchDispatchPostponedActionsAction: PropTypes.func,
  dispatchDismissPostponedActionsAction: PropTypes.func,
  dispatchAppRefreshAllAction: PropTypes.func,
  setPreference: PropTypes.func,
  redirect: PropTypes.func,
  featureFlags: PropTypes.object,
  hasSubscription: PropTypes.bool,
  entitlement: PropTypes.object,
  location: PropTypes.object,
  showIntercom: PropTypes.bool,
  hotjar: PropTypes.bool,
  entitlementLoadPending: PropTypes.bool,
  entitlementIsLoading: PropTypes.bool,
  addFi: PropTypes.bool,
  setupStateIndex: PropTypes.number,
  launchPad: PropTypes.bool,
  accountDrawerDefaultOpen: PropTypes.bool,
  accountsLoadPending: PropTypes.bool,
  flagsLoadPending: PropTypes.bool,
  preferencesLoading: PropTypes.any,
  zeroStateOnNoAccounts: PropTypes.bool,
};

function mapStateToProps(state, _ownProps) {
  const featureFlags = getFeatureFlags(state);
  const isAuthenticated = authSelectors.isAuthenticated(state);
  const profile = getProfile(state);
  const currentPrefs = preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] });

  return {
    isOffline: !getIsOnline(state),

    serviceWorkerEnable: featureFlags.get('serviceWorkerEnable'),
    serviceWorkerUpdateAvailable: getServiceWorkerUpdateIsAvailable(state),
    serviceWorkerAutoUpdate: featureFlags.get('serviceWorkerAutoUpdate'),
    serviceWorkerUserUpdate: featureFlags.get('serviceWorkerUserUpdate'),

    isAuthenticated,
    datasetId: authSelectors.getDatasetId(state),
    addFi: featureFlags && featureFlags.get('addFi'),
    launchPad: featureFlags && featureFlags.get('launchPad'),
    zeroStateOnNoAccounts: featureFlags && featureFlags.get('zeroStateOnNoAccounts'),
    featureFlags,
    flagsLoadPending: getLoadPending(state),
    preferencesLoading: preferencesSelectors.getLoadPending(state),
    muiTheme: getCurrentTheme(state),
    accountDrawerDefaultOpen: currentPrefs.accountDrawerDefaultOpen,
    qCardsOffByDefault: currentPrefs.qCardsOffByDefault,
    setupStateIndex: currentPrefs.setupStateIndex,
    postponedActions: postponedActionsSelectors.getPostponedActions(state),
    accountsById: accountSelectors.getAccountsById(state),
    accountsLoadPending: accountSelectors.getLoadPending(state),
    datasetsById: getDatasetsById(state),
    datasetsLoadPending: getDatasetsLoadPending(state),
    datasetError: getDatasetsError(state),
    entitlement: entitlementSelectors.getTopTierEntitlement(state),
    entitlementIsLoading: entitlementSelectors.isLoading(state),
    entitlementLoadPending: entitlementSelectors.getLoadPending(state),
    showIntercom: featureFlags && featureFlags.get('intercom'),
    email: profile && profile.username,
    hotjar: featureFlags && featureFlags.get('hotjar'),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    doAccountDiscovery: (data) => dispatch(doAccountDiscoveryAction(data)),
    dispatchDispatchPostponedActionsAction: (actions) => dispatch(postponedActionsActions.dispatchPostponedActions(actions)),
    dispatchDismissPostponedActionsAction: (actions) => dispatch(postponedActionsActions.dismissPostponedActions(actions)),
    setPreference: (data) => dispatch(preferencesActions.setPreference(data)),
    redirect: (location) => dispatch(replace(location)),
    dispatchAppRefreshAllAction: (data) => dispatch(appRefreshAll(data)),
  };
}

// These are prop injectors
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));

